import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { translateFn } from '../../../../../utils/translator.helper';
import { FontSizes, FontWeights, ResizeDirection } from '../../../../../types/common/types';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { styles } from '../view/styles';
import { CSSResultGroup } from '@lit/reactive-element/css-tag';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LinkColor } from '../../../../../ui/gzLinkComponent/types';
import { GzLinkWebComponent } from '../../../../../ui/gzLinkComponent';
import { AutomaticAction, Events, getWarningIconNameAndColor, GridColumn, Type, typesWithSettings } from '../types';
import { GzRuleAutomaticActionsGridDataProvider } from '../dataProvider/GzRuleAutomaticActionsGridDataProvider';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { BaseWebComponentTranslator } from '../../../../../framework/BaseWebComponentTranslator';
import { GzSideMenuWebComponent } from '../../../../../ui/gzSideMenu';
import { SideMenuWidthValues } from '../../../../../ui/gzSideMenu/types';
import {
  GzScanAutomaticActionSettingsWebComponent,
} from './scanAutomaticActionSettings/component/GzScanAutomaticActionSettingsWebComponent';
import { GridType } from '../../../../customRulesGrid/types';
import {
  GzKillProcessAutomaticActionSettingsWebComponent,
} from './killProcessAutomaticActionSettings/component/GzKillProcessAutomaticActionSettingsWebComponent';
import {
  GzQuarantineAutomaticActionSettingsWebComponent,
} from './quarantineAutomaticActionSettings/component/GzQuarantineAutomaticActionSettingsWebComponent';
import { GzDataTableWebComponent } from '../../../../../ui/gzDataTable';
import {
  DataTableEvents,
  DecoratedDataTableWebComponent,
  DefaultColumnKeys,
  IDataTableColumn,
} from '../../../../../ui/gzDataTable/types';
import { Feature } from '@nirvana/shared/constants';
import { getDataTableDefaultStrings } from '../../../../../ui/gzDataTable/types/strings';

@customElement('gz-rule-automatic-actions')
export class GzRuleAutomaticActionsWebComponent extends LitElement {
  @property({ type: Object, attribute: false, reflect: false })
  public rule: any;

  @property({ type: Boolean, attribute: false, reflect: false })
  public isGZ: boolean;

  @property({ type: String, attribute: false, reflect: false })
  public gridType: GridType;

  @property({ type: Function, attribute: false, reflect: false })
  public platformHasFeature: (feature: Feature) => boolean;

  private translator = BaseWebComponentTranslator.getInstance();

  @query('#link-to-step2')
  private readonly linkToStep2: GzLinkWebComponent | null;

  @query('#toggle-automatic-actions')
  private readonly toggleAutomaticActionsCheckbox: GzCheckboxWebComponent;

  @query('#grid')
  private readonly grid: GzDataTableWebComponent<AutomaticAction>;

  @query('#edit-settings')
  private readonly editSettingsSideMenu: GzSideMenuWebComponent;

  public static get styles(): CSSResultGroup {
    return styles;
  }

  protected render(): TemplateResult {
    return html`
      <div class="d-flex flex-column">
        <gz-text class="section-title" fontSize="${FontSizes.HEADING_4}" fontweight="${FontWeights.MEDIUM}">
          ${translateFn('Automatic actions')}
        </gz-text>

        ${this.renderEnableGenerateSecurityIncidentsInfoBox()}

        ${this.renderToggleAutomaticActionsCheckbox()}

        ${this.renderDescription()}

        ${this.renderGrid()}
      </div>

      <gz-side-menu
        id="edit-settings"
        resizable="${ResizeDirection.LEFT}"
        menuWidth="${SideMenuWidthValues.MEDIUM}"
        closeOnClickOutside
      />
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);

    if (this.linkToStep2) {
      this.linkToStep2.addEventListener('click', this.onLinkToStep2Click.bind(this));
    }
  }

  private renderEnableGenerateSecurityIncidentsInfoBox(): typeof nothing | TemplateResult {
    return this.rule.generateRca
      ? nothing
      : html`
        <gz-info-box class="mt-3" severity="${InfoBoxSeverity.WARNING}">
          ${unsafeHTML(
            translateFn(
              'To select this option, enable the {bold}Generate security incidents{/bold} in {linkToStep2}Step 2{/linkToStep2}.',
              {
                bold: '<b>',
                '/bold': '</b>',
                linkToStep2: `<gz-link id="link-to-step2" color="${LinkColor.SECONDARY}">`,
                '/linkToStep2': '</gz-link>',
              }
            )
          )}
        </gz-info-box>
      `;
  }

  private renderToggleAutomaticActionsCheckbox(): TemplateResult {
    if (typeof this.rule.enableAutomaticActions === 'undefined') {
      this.rule.enableAutomaticActions = false;
    }

    return html`
      <gz-checkbox
        id="toggle-automatic-actions"
        class="mt-3"
        label="${translateFn('Enable automatic actions')}"
        ?readonly=${!this.rule.generateRca}
        ?checked="${this.rule.enableAutomaticActions}"
        @change="${this.onAutomaticActionsToggle}"
      />
    `;
  }

  private renderDescription(): TemplateResult {
    return html`
      <gz-text
        id="description"
        class="mt-3${!this.rule.generateRca || !this.rule.enableAutomaticActions ? ' disabled' : ''}"
        fontsize="${FontSizes.PARAGRAPH_13}"
      >
        ${translateFn('Select the actions you want to automate.')}
      </gz-text>
    `;
  }

  private renderGrid(): GzDataTableWebComponent {
    const grid = new GzDataTableWebComponent<AutomaticAction>() as DecoratedDataTableWebComponent<AutomaticAction>,
      dataProvider = new GzRuleAutomaticActionsGridDataProvider(
        this.rule,
        this.isGZ,
        this.platformHasFeature,
        {
          [GridColumn.TYPE]: this.renderTypeColumn.bind(this),
          [GridColumn.WARNING]: this.renderWarningColumn.bind(this),
          [GridColumn.DETAILS]: this.renderDetailsColumn.bind(this),
          [GridColumn.EDIT_SETTINGS]: this.renderEditSettingsColumn.bind(this),
        }
      ),
      actions = dataProvider.getActions();

    this.rule.automaticActions = actions;

    grid.id = 'grid';
    grid.className = 'mt-3';
    grid.items = actions;
    grid.columns = dataProvider.getColumns();
    grid.disabled = !this.rule.generateRca || !this.rule.enableAutomaticActions;
    grid.strings = getDataTableDefaultStrings();
    grid.allowRowReordering = true;
    grid.allowRowSelection = true;

    grid.updateComplete.then((): void => {
      actions.forEach((action: AutomaticAction, index: number): void => {
        if (action.enabled) {
          grid.selectItem(index, false);
        }
      });
    });

    grid.addEventListener(DataTableEvents.ROWS_REORDERED, this.onActionsReorder.bind(this));
    grid.addEventListener(DataTableEvents.ROWS_RESET_ORDER, this.onActionsReorder.bind(this));
    grid.addEventListener(DataTableEvents.SELECTION_CHANGED, this.onActionsSelectionChanged.bind(this));

    return grid;
  }

  private renderTypeColumn(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult {
    const typeAsString = this.translator.translateCustomRulesAutomaticActionsType(content as Type);

    return html`<gz-tooltip class="cell-label" tooltipText="${typeAsString}" isEllipsis>${typeAsString}</gz-tooltip>`;
  }

  private renderWarningColumn(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult {
    const type = this.grid.getFormValue()?.[rowIndex].type,
      warning = this.translator.getCustomRulesAutomaticActionsWarning(type);

    if (!warning) {
      return html``;
    }

    const [iconName, iconColor] = getWarningIconNameAndColor(type);

    return html`
      <div class="d-flex align-items-center">
        <gz-icon class="icon" name="${iconName}" color="${iconColor}" size="24"></gz-icon>
        <gz-tooltip class="cell-label" style="margin-left: 6px;" tooltipText="${warning}" isEllipsis>
          ${warning}
        </gz-tooltip>
      </div>
    `;
  }

  private renderDetailsColumn(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult {
    const details = this.translator.getCustomRulesAutomaticActionsDetails(this.grid.getFormValue()?.[rowIndex].type);

    return html`<gz-tooltip class="cell-label" tooltipText="${details}" isEllipsis>${details}</gz-tooltip>`;
  }

  private renderEditSettingsColumn(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult {
    const action = this.grid.getFormValue()?.[rowIndex];

    if (!action || !typesWithSettings.includes(action.type)) {
      return html``;
    }

    const link = new GzLinkWebComponent();

    link.innerHTML = `
      <span class="cell-label" style="color: var(--blue-layout-400-primary);">
        ${translateFn('Edit settings')}
      </span>
    `;

    link.fontSize = FontSizes.PARAGRAPH_10;
    link.callback = (): void => {
      this.onEditSettingsClick(action);
    };

    return html`${link}`;
  }

  private onLinkToStep2Click(): void {
    this.dispatchEvent(new CustomEvent(Events.GO_TO_STEP2));
  }

  private onAutomaticActionsToggle(event: CustomEvent): void {
    this.rule.enableAutomaticActions = event.detail.checked;

    this.requestUpdate('rule');
  }

  private onActionsReorder(event: CustomEvent): void {
    this.rule.automaticActions = event.detail.items.map((actionWithPriority: AutomaticAction): AutomaticAction => {
      const action = {...actionWithPriority};

      delete action[DefaultColumnKeys.PRIORITY];

      return action;
    });
  }

  private onActionsSelectionChanged(event: CustomEvent): void {
    this.rule.automaticActions.forEach((action: AutomaticAction, index: number): void => {
      const enabled = event.detail.items.has(index);

      action.enabled = enabled;
      this.grid.getFormValue()[index].enabled = enabled;
    });
  }

  /**
   * @throws {Error}
   */
  private onEditSettingsClick(action: AutomaticAction): void {
    let component;

    switch (action.type) {
      case Type.KILL_PROCESS:
        component = new GzKillProcessAutomaticActionSettingsWebComponent();

        component.rule = this.rule;
        component.isGZ = this.isGZ;
        component.gridType = this.gridType;
        component.areAutomaticActionsAvailable = this.platformHasFeature(
          Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS
        );

        break;

      case Type.SCAN:
        component = new GzScanAutomaticActionSettingsWebComponent();

        break;

      case Type.QUARANTINE:
        component = new GzQuarantineAutomaticActionSettingsWebComponent();

        component.rule = this.rule;
        component.isGZ = this.isGZ;
        component.gridType = this.gridType;
        component.areAutomaticActionsAvailable = this.platformHasFeature(
          Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS
        );

        break;

      default:
        throw new Error(`Automatic action with type "${action.type}" does not have settings to be edited.`);
    }

    component.settings = action.settings;
    component.closeSideMenuCallback = (): void => {
      this.editSettingsSideMenu.closeSideMenu();
    };

    this.editSettingsSideMenu.setMenuContent(component);
    this.editSettingsSideMenu.openSideMenu();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rule-automatic-actions': GzRuleAutomaticActionsWebComponent;
  }
}
